import { onDestroy, onMount } from "@/modules/_";
import gsap, { reduced } from "@lib/gsap";
import { Resize } from "@lib/subs";
import { gsapEase } from "@utils/easings";

// A cascading row of cards: one is open, the rest sit beside it as
// progressively narrower vertical strips. The row always fills the container
// exactly — widths are flex-grow weights on a zero basis, so the proportions
// hold at every frame of the transition instead of only at the ends, and the
// gaps come out of the browser's arithmetic rather than ours.
//
// A strip is a window, not a shrunken card: every card's inner composition
// keeps the open card's width and is centred inside the item, so a strip shows
// a slice of the same artwork at the same scale. Opening one widens the window.
//
// Cards never move. They hold their order and their neighbours, and opening one
// only redistributes width — the card expands in place while the others give
// theirs up, stepping down the further they sit from the open one.
//
// Below SLIDER_QUERY none of that survives — a sliver of a composition on a
// phone is noise — so the row turns into a scroll-snap carousel of full-width
// cards, swiped instead of tapped, and every card's player stays live.
//
// Interaction is two-step by design. A closed card is a target, not a player:
// everything inside it is inert, so the first click only opens it. Once open,
// the card's own controls take over and the next click plays, scrubs or
// fullscreens. Closing a card pauses it and fades its controls back out.
//
//   [data-module="video-cascade"]      <- row wrapper
//     [video-cascade="item"]           <- card; the centre one opens on load
//       [video-cascade="inner"]        <- fixed-size composition; falls back to
//                                         the item's first element child
//
// Optional tuning on the wrapper — weights run open, then each step outward,
// with the last value repeating for everything further:
//   data-weights="100,12"        <- equal strips (default)
//   data-weights="100,21,8"      <- strips stepping down toward the edges
//   data-weights-mobile="100,8"
//   data-ratio="16/9"         <- reserved shape of a card (default 16/9)
//   data-max-vh="80"          <- height ceiling as a % of the viewport
//   data-start="0"            <- 0-based card to open on load (default: centre)
//   data-gap="24"             <- px between cards (default 24; "" keeps the CSS)

// Weights step down for tablets; below SLIDER_QUERY the cascade gives way to a
// swipeable slider entirely.
const MOBILE_QUERY = "(max-width: 991px)";
const SLIDER_QUERY = "(max-width: 767px)";

// A slide leaves the neighbours peeking, so the row reads as swipeable.
const SLIDE_WIDTH_PCT = 86;
const SLIDER_GAP_PX = 12;

const GAP_PX = 24;

// The row's height comes from the video-player: it sets padding-top on
// [data-video="height"] as a percentage of the video's aspect, and percentage
// padding resolves against width — so pinning every composition to the open
// card's width is what makes the cards match. That padding is 0 until
// loadedmetadata fires, though, and stays 0 if a video never loads, which
// collapses the row. So the cascade reserves the shape itself: a min-height
// holds the space from the first frame (and keeps the videos from shifting the
// page in), a max-height stops a wide viewport from running the cards off the
// screen. A video whose real aspect is taller grows into the gap between them.
const RATIO = 16 / 9;
const MAX_VH = 0.8;

function parseRatio(value: string | undefined) {
  if (!value) return RATIO;
  const [w, h] = value.split(/[/:]/).map((part) => parseFloat(part.trim()));
  if (Number.isFinite(w) && Number.isFinite(h) && h > 0) return w / h;
  return Number.isFinite(w) && w > 0 ? w : RATIO;
}

// Controls belong to the open card. On a strip they'd be a sliver of floating
// chrome over the artwork, so they fade out entirely — raise this to dim them
// instead of hiding them. The fade itself is the video-player's own 200ms
// opacity transition; we only set the value.
const INACTIVE_CONTROLS_OPACITY = 0;

// The poster's play button sits outside [data-video="controls"], so it survives
// the fade above — it gets dimmed rather than hidden, since it's the only cue
// that a strip is a video at all.
const INACTIVE_PLAY_OPACITY = 0.35;
const PLAY_TRANSITION = "opacity 200ms ease";

// Open card, then every other card. The last value repeats outward, so two
// entries means every strip is the same width; add a third and fourth to step
// them down the further they sit from the open card.
const WEIGHTS = [100, 12];
const WEIGHTS_MOBILE = [100, 8];

const DURATION = 0.7;
const EASE = gsapEase("energy");

const ACTIVE_CLASS = "is--active";

function parseWeights(value: string | undefined, fallback: number[]) {
  if (!value) return fallback;
  const parsed = value
    .split(",")
    .map((part) => parseFloat(part.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
  return parsed.length > 0 ? parsed : fallback;
}

export default function (element: HTMLElement, dataset: DOMStringMap) {
  const items = Array.from(
    element.querySelectorAll<HTMLElement>('[video-cascade="item"]'),
  );
  if (items.length === 0) return;

  const total = items.length;

  const inners = items.map(
    (item) =>
      item.querySelector<HTMLElement>('[video-cascade="inner"]') ??
      (item.firstElementChild as HTMLElement | null) ??
      item,
  );

  // A card can carry more than one controls bar (e.g. a second one for the
  // expanded view), and the video-player module wires them all.
  const controlBars = items.map((item) =>
    Array.from(item.querySelectorAll<HTMLElement>('[data-video="controls"]')),
  );

  // The button wrapping the poster's play icon. Reached through the icon rather
  // than by class, so the shared video-player markup stays untouched.
  const playButtons = items.map((item) =>
    Array.from(
      item.querySelectorAll<HTMLElement>(
        '[data-video="poster"] [data-video="play-icon"]',
      ),
    ).map((icon) => (icon.parentElement as HTMLElement | null) ?? icon),
  );

  const ratio = parseRatio(dataset.ratio);
  const maxVh = (parseFloat(dataset.maxVh ?? "") || MAX_VH * 100) / 100;

  const weights = parseWeights(dataset.weights, WEIGHTS);
  const weightsMobile = parseWeights(dataset.weightsMobile, WEIGHTS_MOBILE);

  const mobileQuery = window.matchMedia(MOBILE_QUERY);
  const sliderQuery = window.matchMedia(SLIDER_QUERY);

  // Even counts have no true centre; the open card takes the left of the two.
  const centreIndex = Math.floor((total - 1) / 2);
  const startIndex = parseInt(dataset.start ?? "", 10);

  let activeIndex = Number.isFinite(startIndex)
    ? Math.max(0, Math.min(total - 1, startIndex))
    : centreIndex;
  let growTween: gsap.core.Tween | null = null;
  let mode: "cascade" | "slider" | null = null;
  let resizeSub: (() => void) | null = null;
  let modeResizeSub: (() => void) | null = null;
  const cleanups: Array<() => void> = [];
  const styles = new Map<HTMLElement, Partial<CSSStyleDeclaration>>();

  function activeWeights() {
    return mobileQuery.matches ? weightsMobile : weights;
  }

  // The open card's weight, plus one fixed budget shared by every other card.
  // Distance weights alone would total more at the ends than in the middle (an
  // end card has no neighbour on one side), so the open card would be wider
  // there than the composition pinned inside it, and the surplus would read as
  // a broken gap. Holding the budget constant keeps the open width identical
  // wherever the card sits; only the strips redistribute.
  function weightBudget() {
    const list = activeWeights();
    let rest = 0;
    for (let depth = 1; depth < total; depth++) {
      rest += list[Math.min(depth, list.length - 1)];
    }
    return { open: list[0], rest, total: list[0] + rest };
  }

  // Width by distance from the open card — the last weight covers everything
  // further out, so a long row just keeps its outer cards at sliver width —
  // then scaled to fit the budget above.
  function weightsForActive() {
    const list = activeWeights();
    const { open: openWeight, rest } = weightBudget();
    const raw = items.map((_, index) =>
      index === activeIndex
        ? 0
        : list[Math.min(Math.abs(index - activeIndex), list.length - 1)],
    );
    const rawTotal = raw.reduce((sum, value) => sum + value, 0);
    return raw.map((value, index) =>
      index === activeIndex
        ? openWeight
        : rawTotal > 0
          ? (value * rest) / rawTotal
          : 0,
    );
  }

  // Every composition is sized to the open card and centred in its item, so a
  // narrow item clips it evenly on both sides instead of scaling it. Derived
  // from the weights rather than measured off the open card, so a transition in
  // flight can't feed a half-open width back in.
  function measure() {
    const style = getComputedStyle(element);
    const gap = parseFloat(style.columnGap) || 0;
    const padding =
      (parseFloat(style.paddingLeft) || 0) +
      (parseFloat(style.paddingRight) || 0);
    const available = element.clientWidth - padding - gap * (total - 1);
    if (available <= 0) return;

    const budget = weightBudget();
    const openWidth = (available * budget.open) / budget.total;
    for (const inner of inners) inner.style.width = `${openWidth}px`;

    const maxHeight = window.innerHeight * maxVh;
    element.style.minHeight = `${Math.min(openWidth / ratio, maxHeight)}px`;
    element.style.maxHeight = `${maxHeight}px`;
  }

  function pauseVideo(item: HTMLElement) {
    const video = item.querySelector<HTMLVideoElement>('video[data-video="element"]');
    if (video && !video.paused) video.pause();
  }

  function applyGrow(values: number[]) {
    items.forEach((item, index) => {
      item.style.flexGrow = String(values[index]);
    });
  }

  // One tween for the whole row rather than one per card: every weight moves on
  // the same eased progress, and both the start and end sets sum to the same
  // total, so the widths add up to the container at every frame — not just at
  // the ends. (gsap.to can't drive flexGrow directly; only gsap.set writes it.)
  function tweenGrow(targets: number[]) {
    growTween?.kill();
    const from = items.map((item, index) =>
      parseFloat(item.style.flexGrow || "") || targets[index],
    );
    const state = { t: 0 };
    growTween = gsap.to(state, {
      t: 1,
      duration: DURATION,
      ease: EASE,
      onUpdate: () =>
        applyGrow(from.map((start, i) => start + (targets[i] - start) * state.t)),
    });
  }

  function render(animate: boolean) {
    const targets = weightsForActive();
    if (animate && !reduced) tweenGrow(targets);
    else {
      growTween?.kill();
      applyGrow(targets);
    }

    items.forEach((item, index) => {
      const open = index === activeIndex;

      item.classList.toggle(ACTIVE_CLASS, open);
      // A strip is a target, not a player: swallowing pointer events inside it
      // means a click lands on the item and opens it, rather than hitting the
      // video underneath.
      inners[index].style.pointerEvents = open ? "" : "none";
      item.style.cursor = open ? "" : "pointer";
      // The artwork is hidden from assistive tech while it's a strip, but the
      // item itself stays exposed — it's the button that opens the card, so
      // aria-hidden must not land on it.
      inners[index].setAttribute("aria-hidden", String(!open));

      for (const button of playButtons[index]) {
        button.style.opacity = open ? "1" : String(INACTIVE_PLAY_OPACITY);
      }

      for (const bar of controlBars[index]) {
        // Set, don't tween: the video-player puts its own opacity transition on
        // these, and two writers on one property fight.
        bar.style.opacity = open ? "1" : String(INACTIVE_CONTROLS_OPACITY);
      }

      if (open) {
        item.removeAttribute("role");
        item.removeAttribute("tabindex");
        item.removeAttribute("aria-label");
      } else {
        item.setAttribute("role", "button");
        item.setAttribute("tabindex", "0");
        item.setAttribute("aria-label", `Show video ${index + 1} of ${total}`);
        pauseVideo(item);
      }
    });
  }

  function open(index: number, animate = true) {
    // Clamped, not wrapped: the row has ends now that nothing rotates.
    const next = Math.max(0, Math.min(total - 1, index));
    if (next === activeIndex) return;
    activeIndex = next;
    render(animate);
  }

  function step(delta: number) {
    open(activeIndex + delta);
  }

  function setupCascade() {
    styles.set(element, {
      display: element.style.display,
      alignItems: element.style.alignItems,
      columnGap: element.style.columnGap,
      minHeight: element.style.minHeight,
      maxHeight: element.style.maxHeight,
    });
    element.style.display = "flex";
    element.style.alignItems = "stretch";
    // measure() reads the computed gap back out, so the widths stay exact
    // whether the gap comes from here or from the stylesheet.
    const gap = parseFloat(dataset.gap ?? "");
    if (dataset.gap !== "") {
      element.style.columnGap = `${Number.isFinite(gap) ? gap : GAP_PX}px`;
    }

    items.forEach((item, index) => {
      styles.set(item, {
        flexGrow: item.style.flexGrow,
        flexBasis: item.style.flexBasis,
        minWidth: item.style.minWidth,
        overflow: item.style.overflow,
        display: item.style.display,
        justifyContent: item.style.justifyContent,
      });
      // Zero basis: the widths are purely the grow weights, so they always sum
      // to the row no matter what the cards contain.
      item.style.flexBasis = "0%";
      item.style.minWidth = "0";
      // The window that clips the composition.
      item.style.overflow = "hidden";
      item.style.display = "flex";
      item.style.justifyContent = "center";

      for (const button of playButtons[index]) {
        styles.set(button, {
          transition: button.style.transition,
          opacity: button.style.opacity,
        });
        button.style.transition = PLAY_TRANSITION;
      }

      const inner = inners[index];
      styles.set(inner, {
        flex: inner.style.flex,
        width: inner.style.width,
        maxWidth: inner.style.maxWidth,
        pointerEvents: inner.style.pointerEvents,
      });
      // Fixed size — the composition must not shrink with its item.
      inner.style.flex = "none";
      inner.style.maxWidth = "none";

      // Capture, not bubble. pointer-events is inherited, not a barrier: a
      // descendant that sets `auto` — .video-bg, .video-player_timeline and the
      // trial button all do — punches straight back through the `none` on the
      // inner, and the click reaches the poster, which plays the video. Catching
      // it on the way down and stopping it there is the only guard the page's
      // CSS can't undo.
      const onClick = (event: MouseEvent) => {
        if (index === activeIndex) return;
        event.preventDefault();
        event.stopPropagation();
        open(index);
      };
      // Scrubbing starts on pointerdown, so that has to be stopped too — but
      // never cancelled: preventDefault here can suppress the click that
      // follows, and the click is what opens the card.
      const onPointerDown = (event: PointerEvent) => {
        if (index === activeIndex) return;
        event.stopPropagation();
      };
      const onKey = (event: KeyboardEvent) => {
        if (index === activeIndex) return;
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        open(index);
      };
      item.addEventListener("click", onClick, true);
      item.addEventListener("pointerdown", onPointerDown, true);
      item.addEventListener("mousedown", onPointerDown as EventListener, true);
      item.addEventListener("keydown", onKey);
      cleanups.push(() => {
        item.removeEventListener("click", onClick, true);
        item.removeEventListener("pointerdown", onPointerDown, true);
        item.removeEventListener("mousedown", onPointerDown as EventListener, true);
        item.removeEventListener("keydown", onKey);
      });
    });

    const onArrows = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") step(-1);
      else if (event.key === "ArrowRight") step(1);
      else return;
      event.preventDefault();
    };
    element.addEventListener("keydown", onArrows);
    cleanups.push(() => element.removeEventListener("keydown", onArrows));

    measure();
    render(false);

    // The compositions are sized in pixels off the container, so they need
    // re-deriving whenever it changes — including a breakpoint crossing, which
    // also swaps the weights.
    const onResize = () => {
      measure();
      render(false);
    };
    resizeSub = Resize.add(onResize);
    mobileQuery.addEventListener("change", onResize);
    cleanups.push(() => mobileQuery.removeEventListener("change", onResize));

    // Late-loading media can change the row's height but not the weights, so
    // only the pixel width needs revisiting.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    }
  }

  /* --------------------------------------------------------------- slider */

  // Below the breakpoint the cascade has nothing to show: a sliver of a
  // composition on a 390px screen is noise. The row becomes a scroll-snap
  // carousel instead — one card at a time, swiped rather than tapped, with the
  // browser doing the scrolling. Nothing is inert here, so a tap goes straight
  // to the player: swiping is how you change cards, which is what makes the
  // two-step guard unnecessary.
  function setupSlider() {
    styles.set(element, {
      display: element.style.display,
      alignItems: element.style.alignItems,
      columnGap: element.style.columnGap,
      minHeight: element.style.minHeight,
      maxHeight: element.style.maxHeight,
      overflowX: element.style.overflowX,
      overflowY: element.style.overflowY,
      scrollSnapType: element.style.scrollSnapType,
    });
    element.style.display = "flex";
    element.style.alignItems = "stretch";
    element.style.columnGap = `${SLIDER_GAP_PX}px`;
    element.style.overflowX = "auto";
    // Vertical scroll stays with the page.
    element.style.overflowY = "hidden";
    element.style.scrollSnapType = "x mandatory";
    // Hidden where it's supported; a visible bar under the cards is only a
    // cosmetic loss elsewhere.
    (element.style as any).scrollbarWidth = "none";

    items.forEach((item, index) => {
      styles.set(item, {
        flexGrow: item.style.flexGrow,
        flexShrink: item.style.flexShrink,
        flexBasis: item.style.flexBasis,
        minWidth: item.style.minWidth,
        overflow: item.style.overflow,
        scrollSnapAlign: item.style.scrollSnapAlign,
      });
      item.style.flexGrow = "0";
      item.style.flexShrink = "0";
      item.style.flexBasis = `${SLIDE_WIDTH_PCT}%`;
      item.style.minWidth = "0";
      item.style.overflow = "hidden";
      item.style.scrollSnapAlign = "center";

      // The composition fills its card here rather than overflowing it — a
      // slide is the whole video, not a window onto part of one.
      const inner = inners[index];
      styles.set(inner, {
        flex: inner.style.flex,
        width: inner.style.width,
        maxWidth: inner.style.maxWidth,
        pointerEvents: inner.style.pointerEvents,
      });
      inner.style.flex = "none";
      inner.style.width = "100%";
      inner.style.maxWidth = "none";
    });

    // Nothing is dimmed in this mode — every card is a live player — so drop any
    // opacity the cascade left behind and hand the controls back to the player.
    controlBars.forEach((bars) =>
      bars.forEach((bar) => bar.style.removeProperty("opacity")),
    );
    playButtons.forEach((buttons) =>
      buttons.forEach((button) => button.style.removeProperty("opacity")),
    );

    measureSlider();
    const onResize = () => measureSlider();
    resizeSub = Resize.add(onResize);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measureSlider);
    }
  }

  // Same height reservation as the cascade, off the slide width instead of the
  // open-card width.
  function measureSlider() {
    const slideWidth = (element.clientWidth * SLIDE_WIDTH_PCT) / 100;
    if (slideWidth <= 0) return;
    const maxHeight = window.innerHeight * maxVh;
    element.style.minHeight = `${Math.min(slideWidth / ratio, maxHeight)}px`;
    element.style.maxHeight = `${maxHeight}px`;
  }

  /* ----------------------------------------------------------------- swap */

  // Both setups record every inline style they write, so teardown restores the
  // markup either way and the module can switch modes live.
  function teardown() {
    resizeSub?.();
    resizeSub = null;
    cleanups.forEach((fn) => fn());
    cleanups.length = 0;

    growTween?.kill();
    growTween = null;
    items.forEach((item) => {
      item.style.cursor = "";
      item.classList.remove(ACTIVE_CLASS);
      ["role", "tabindex", "aria-label"].forEach((attr) =>
        item.removeAttribute(attr),
      );
    });

    inners.forEach((inner) => inner.removeAttribute("aria-hidden"));
    controlBars.forEach((bars) =>
      bars.forEach((bar) => bar.style.removeProperty("opacity")),
    );
    playButtons.forEach((buttons) =>
      buttons.forEach((button) => button.style.removeProperty("opacity")),
    );

    styles.forEach((saved, el) => {
      for (const [prop, value] of Object.entries(saved)) {
        (el.style as any)[prop] = value ?? "";
      }
    });
    styles.clear();
    (element.style as any).scrollbarWidth = "";
  }

  function applyMode(next: "cascade" | "slider") {
    if (mode === next) return;
    if (mode) teardown();
    mode = next;
    if (next === "cascade") setupCascade();
    else setupSlider();
  }

  function chooseMode() {
    applyMode(sliderQuery.matches ? "slider" : "cascade");
  }

  onMount(() => {
    chooseMode();
    sliderQuery.addEventListener("change", chooseMode);
    // applyMode is a no-op when the mode hasn't changed, so a resize that
    // doesn't cross the breakpoint costs nothing.
    modeResizeSub = Resize.add(chooseMode);
  });

  onDestroy(() => {
    sliderQuery.removeEventListener("change", chooseMode);
    modeResizeSub?.();
    modeResizeSub = null;
    teardown();
    mode = null;
  });
}
